/**
 * IdleControls - Steuerung im IDLE-Zustand (Pause zwischen Sessions)
 *
 * Zeigt:
 * - "Arbeit fortsetzen" Button (gruen) - startet neue Timer fuer alle bisherigen Mitarbeiter
 * - "Tagesabschluss" Button (sekundaer) - sperrt den Tag
 * - Zusammenfassung der bisherigen Arbeitszeit
 */

import { computed } from '@preact/signals';
import { Play, Clock, Users } from 'lucide-preact';
import {
  resumeDay,
  lockDay,
  entries,
  sheetStats,
  isLoading,
  isCapturingLocation,
} from '../../stores/timesheet.store';
import { SwipeButton } from '../../components/ui/SwipeButton';
import styles from './IdleControls.module.css';

export function IdleControls() {
  // Berechne Anzahl der WORK-Mitarbeiter (unique)
  const workEmployeeCount = computed(() => {
    const workEntries = entries.value.filter((e) => e.type === 'WORK');
    const uniqueEmployees = new Set(workEntries.map((e) => e.employeeId));
    return uniqueEmployees.size;
  });

  // Anzahl der Sessions (Start/Stop-Zyklen) heute
  // Alle Entries einer Session haben denselben startTime -> unique startTimes zählen
  const sessionCount = computed(() => {
    const completedWork = entries.value.filter((e) => e.type === 'WORK' && e.status === 'COMPLETED');
    const uniqueStartTimes = new Set(completedWork.map((e) => e.startTime));
    return uniqueStartTimes.size;
  });

  // Handler
  async function handleResume() {
    await resumeDay();
  }

  async function handleLockDay() {
    await lockDay();
  }

  return (
    <div className={styles.container}>
      {/* Summary Section */}
      <div className={styles.summary}>
        <div className={styles.summaryItem}>
          <Clock size={20} className={styles.summaryIcon} />
          <div className={styles.summaryContent}>
            <span className={styles.summaryValue}>{sheetStats.value.totalNetFormatted}</span>
            <span className={styles.summaryLabel}>Arbeitszeit heute</span>
          </div>
        </div>
        <div className={styles.summaryItem}>
          <Users size={20} className={styles.summaryIcon} />
          <div className={styles.summaryContent}>
            <span className={styles.summaryValue}>{workEmployeeCount.value}</span>
            <span className={styles.summaryLabel}>Mitarbeiter</span>
          </div>
        </div>
      </div>

      {/* Status Message */}
      <div className={styles.statusMessage}>
        <span>Pause - {sessionCount.value} Session(s) abgeschlossen</span>
      </div>

      {/* Action Buttons */}
      <div className={styles.actions}>
        <SwipeButton
          label="Arbeit fortsetzen"
          loadingLabel="Starte Timer..."
          onConfirm={handleResume}
          disabled={isLoading.value || workEmployeeCount.value === 0}
          loading={isCapturingLocation.value}
          variant="success"
        />

        <SwipeButton
          label="Tagesabschluss / Feierabend"
          loadingLabel="Schliesse ab..."
          onConfirm={handleLockDay}
          disabled={isLoading.value}
          loading={isCapturingLocation.value}
          variant="primary"
        />
      </div>

      {/* Help Text */}
      <p className={styles.helpText}>
        <Play size={14} />
        <span>"Arbeit fortsetzen" startet neue Timer für alle {workEmployeeCount.value} Mitarbeiter</span>
      </p>
    </div>
  );
}
